import { gql } from '@apollo/client';

import { fragments as procedureTypeFragments } from './procedureTypes';
import { fragments as physicianFragments } from './physicians';
import { fragments as anesthesiologistFragments } from './anesthesiologists';
import { fragments as patientFragments } from './patients';

const procedureBaseFields = `
  id
  visitNumber
  procedureHstId
  status
  startTime
  startTimeText
  endTimeText
  duration
  orId
  or
  isCanceled
  physicianId
  physicianHstId
  physicianName
  physicianNameHST
  physicianColor
  patientId
  patientHstId
  patientName
  patientNameUnformatted
  patientInitials
  patientDateOfBirth
  patientDateOfBirthISO
  patientAge
  patientSex
  patientHomePhone
  patientCellPhone
  caretakerCellPhone
  familyCellPhone
  patientEmailAddress
  procedureTypeId
  procedureType
  procedureMod
  diagnosis
  equipment
  cptCodes
  bilaterally
  visitType
  room
  insuranceName
  policyName
  anesthesiologistId
  anesthesiologistHstId
  anesthesiologistName
  anesthesiaType
  allergies
  notes
  visit
  preferredLanguage
  chartTemplateVersion
`;

const allButRoom = gql`
  fragment AllButRoomPatientData on Patient {
    id
    hstId
    name
    initials
    braceletId
    type
    notificationNumbers
    procedure {
      id
      procedureHstId
      preOpNote
      startTime
      procedurePriority
    }
    procedureType {
      ...ProcedureTypeData
    }
    physician {
      ...PhysicianData
    }
    status
    phoneNumber
    bed
    operationRoom {
      id
      name
    }
    preOpBed
    pacuBed
    priority
    icon
    color
    createdAt
    changedRoomAt
    dischargedAt
    voidedAt
    note
    caretakerConsent
    caretakerName
    caretakerNote
    physicianLastPatient
    anesthesiologistLastPatient
    caretakerMessages {
      phoneNumber
      content
    }
    caretakerThread {
      id
      hasUnreadMessages
    }
    patientThread {
      id
      hasUnreadMessages
    }
    preOpNote
    pacuNote
    blockNerveFollow
    oobWarningDelayReason
    ptWarningDelayReason
    ptCompleteWarningDelayReason
    pacuOutWarningDelayReason
    medsDeliveryStatus
  }
  ${procedureTypeFragments.all}
  ${physicianFragments.all}
`;

const all = gql`
  fragment PatientData on Patient {
    ...AllButRoomPatientData
    room {
      id
      name
      type
      oobWarningThreshold
      ptWarningThreshold
      ptCompleteWarningThreshold
      pacuOutWarningThreshold
    }
  }
  ${allButRoom}
`;

export const fragments = {
  allButRoom,
  all,
  patientEvents: gql`
    fragment PatientEvents on Patient {
      events {
        admittedAt
        wrAt
        wrOutAt
        preOpAt
        blockNerveAt
        preOpOutAt
        orAt
        orOutAt
        cleaningCompletedAt
        orRoomReadyAt
        recoveryAt
        recoveryOutAt
        postOpAt
        postOpOutAt
        dpuAt
        ableToVisitPACUAt
        ableToVisitPOSTOPAt
        dischargedAt
        readyForAnesthesiaAt
        spinalTimeoutAt
        readyForSurgeonAt
        readyForOrAt
        holdProcedureAt
        anestheticStartAt
        physicianInAt
        timeOutAt
        antibioticTimeAt
        readyForSurgeryAt
        procedureStartAt
        calledNextPatientAt
        closingStartAt
        surgeonLeftAt
        procedureEndAt
        anestheticEndAt
        helpAt
        dressingOnAt
        transferredToChairAt
        transferredToPTAt
        ptCompleteAt
        helpActiveAt
        flipRoomActiveAt
        blockNerveActiveAt
      }
    }
  `,
  roomTimes: gql`
    fragment RoomTimes on Patient {
      waitingRoomTime
      prepTime
      orTime
      pacuTime
      postopTime
    }
  `,
  patientLog: gql`
    fragment PatientLog on Patient {
      log {
        id
        entries {
          type
          room {
            id
            name
            type
          }
          manualEntryBy {
            id
            name
            superRole
          }
          manualTimestamp
          status
          delivery
          value
          performedBy {
            id
            name
            superRole
          }
        }
        timestamp
        createdBy {
          id
          name
          superRole
        }
        createdAt
      }
    }
  `,
};

export const list = gql`
  query patients {
    patients {
      ...PatientData
      ...PatientLog
    }
  }
  ${fragments.all}
  ${fragments.patientLog}
`;

export const nextPatientMRN = gql`
  query nextPatientMRN {
    nextPatientMRN
  }
`;

export const patientSubscription = gql`
  subscription patients {
    patients {
      ...PatientData
      ...PatientLog
    }
  }
  ${fragments.all}
  ${fragments.patientLog}
`;

export const patientPickerSubscription = gql`
  subscription patients {
    patients {
      ...PatientData
      ...PatientLog
      procedure {
        id
        patientSex
        patientAge
      }
    }
  }
  ${fragments.all}
  ${fragments.patientLog}
`;

export const patientPickerTodaySubscription = gql`
  subscription patientsToday($roomId: Long) {
    patients: patientsToday(roomId: $roomId) {
      ...PatientData
      ...PatientLog
      procedure {
        id
        patientSex
        patientAge
        anesthesiaType
        anesthesiologist {
          id
          name
        }
      }
    }
  }
  ${fragments.all}
  ${fragments.patientLog}
`;

export const listActiveAndPastPatients = gql`
  subscription patients($filter: Filter) {
    patients: activeAndPastPatients(filter: $filter) {
      procedure {
        startTime
        duration
        patientAge
        patientSex
      }
      dischargedByDisappearance
      ...PatientData
      ...PatientEvents
    }
  }
  ${fragments.all}
  ${fragments.patientEvents}
`;

export const listPacuPrepPostopPatients = gql`
  subscription listPacuPrepPostopPatients(
    $roomType: RoomType
    $roomId: Long
    $includePreOpNurse: Boolean! = false
    $includePacuNurse: Boolean! = false
  ) {
    patients: listPacuPrepPostopPatients(roomType: $roomType, roomId: $roomId) {
      transferredToChair
      transferredToPT
      ptComplete
      preOpNurse @include(if: $includePreOpNurse) {
        id
        staffId
        name
        primarySpecialty
      }
      pacuNurse @include(if: $includePacuNurse) {
        id
        staffId
        name
        primarySpecialty
      }
      ...PatientData
      ...PatientEvents
      anesthesiologist {
        ...AnesthesiologistData
      }
      log {
        id
        entries {
          type
          room {
            id
            name
            type
          }
          manualEntryBy {
            id
            name
          }
          status
        }
        timestamp
      }
    }
  }
  ${fragments.all}
  ${fragments.patientEvents}
  ${anesthesiologistFragments.all}
`;

export const listOrPacuPatients = gql`
  subscription {
    patients: listOrPacuPatients {
      ...AllButRoomPatientData
      room {
        id
        name
        oobWarningThreshold
        ptWarningThreshold
        ptCompleteWarningThreshold
        pacuOutWarningThreshold
      }
      pacuNurse {
        id
        staffId
        name
        primarySpecialty
      }
      anesthesiologist {
        ...AnesthesiologistData
      }
      transferredToChair
      transferredToPT
      ptComplete
      ...PatientLog
    }
  }
  ${fragments.allButRoom}
  ${anesthesiologistFragments.all}
  ${patientFragments.patientLog}
`;

export const listActivePatients = gql`
  subscription {
    patients: listActivePatients {
      ...PatientData
      anesthesiologist {
        ...AnesthesiologistData
      }
      log {
        id
        entries {
          type
          room {
            id
            name
            type
          }
          manualEntryBy {
            id
            name
          }
          status
        }
        timestamp
      }
    }
  }
  ${fragments.all}
  ${anesthesiologistFragments.all}
`;

const ChartingData = `
  id
  questionnaire {
    id
    name
    icon
    type
    content
    order
    version
  }
  questionnaireExchange {
    id
    procedureId
    answers
    submittedAt
  }
  completed
  defaultAnswers
`;

const ChartingsData = `
  preOpCharts {
    ${ChartingData}
  }
  orCharts {
    ${ChartingData}
  }
  pacuCharts {
    ${ChartingData}
  }
`;

export const subscription = gql`
  subscription patient($id: Long!) {
    patient(id: $id) {
      ...PatientData
      ...RoomTimes
      ...PatientLog
      procedure {
        rideHomeCellPhone
        ${procedureBaseFields}
        entryQuestionnaire {
          id
          questionnaireVersion
          progress
          questions
          answers
          progress
          submittedAt
        }
        ${ChartingsData}
        questionnaireInviteSMSCount
        questionnaireInviteEmailCount
        events {
          id
          type
          timestamp
          patientPin
          channel
          content
          user {
            id
            name
          }
          text
          status
          anesthesiaReviewed
          exchangeId
        }
      }
      orRoomTimes {
        enteredAt
        exitedAt
      }
    }
  }
  ${fragments.all}
  ${fragments.roomTimes}
  ${fragments.patientLog}
`;

export const subscriptionWithPhysician = gql`
  subscription physicianPatient($id: Long!, $physicianIds: [Long!]!) {
    patient: physicianPatient(id: $id, physicians: $physicianIds) {
      ...PatientData
      ...RoomTimes
      ...PatientLog
      procedure {
        rideHomeCellPhone
        ${procedureBaseFields}
        entryQuestionnaire {
          id
          questionnaireVersion
          progress
          questions
          answers
          progress
          submittedAt
        }
        ${ChartingsData}
        questionnaireInviteSMSCount
        questionnaireInviteEmailCount
        events {
          id
          type
          timestamp
          patientPin
          channel
          content
          user {
            id
            name
          }
          text
          status
          anesthesiaReviewed
          exchangeId
        }
      }
      orRoomTimes {
        enteredAt
        exitedAt
      }
    }
  }
  ${fragments.all}
  ${fragments.roomTimes}
  ${fragments.patientLog}
`;

export const item = gql`
  query patient($id: Long!) {
    patient(id: $id) {
      ...PatientData
      ...RoomTimes
      ...PatientLog
      procedure {
        id
      }
    }
  }
  ${fragments.all}
  ${fragments.roomTimes}
  ${fragments.patientLog}
`;

export const pastItem = gql`
  query patient($id: Long!) {
    pastPatient(id: $id) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const create = gql`
  mutation createPatient(
    $name: String
    $procedureId: Long
    $braceletId: String!
    $type: PatientType
    $notificationNumbers: String
    $phoneNumber: String
    $procedureType: Long
    $physician: Long!
    $autoBracelet: Boolean
    $note: String
    $caretakerConsent: Boolean
    $caretakerName: String
    $caretakerNote: String
  ) {
    createPatient(
      name: $name
      procedureId: $procedureId
      braceletId: $braceletId
      type: $type
      notificationNumbers: $notificationNumbers
      phoneNumber: $phoneNumber
      procedureType: $procedureType
      physician: $physician
      autoBracelet: $autoBracelet
      note: $note
      caretakerConsent: $caretakerConsent
      caretakerName: $caretakerName
      caretakerNote: $caretakerNote
    ) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const update = gql`
  mutation editPatient(
    $id: Long!
    $name: String
    $braceletId: String
    $type: PatientType
    $notificationNumbers: String
    $phoneNumber: String
    $procedureType: Long
    $physician: Long
    $roomId: Long
    $icon: String
    $color: String
    $note: String
    $caretakerConsent: Boolean
    $caretakerName: String
    $caretakerNote: String
  ) {
    editPatient(
      id: $id
      name: $name
      braceletId: $braceletId
      type: $type
      notificationNumbers: $notificationNumbers
      phoneNumber: $phoneNumber
      procedureType: $procedureType
      physician: $physician
      manualRoomId: $roomId
      icon: $icon
      color: $color
      note: $note
      caretakerConsent: $caretakerConsent
      caretakerName: $caretakerName
      caretakerNote: $caretakerNote
    ) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const remove = gql`
  mutation deletePatient($id: Long!) {
    deletePatient(id: $id)
  }
`;

export const assignBed = gql`
  mutation assignBed($id: Long!, $bed: String) {
    assignBed(id: $id, bed: $bed)
  }
`;

export const unassignBed = gql`
  mutation unassignBed($id: Long!, $bed: String, $roomId: Long) {
    unassignBed(id: $id, bed: $bed, roomId: $roomId)
  }
`;

export const assignBedInRoom = gql`
  mutation assignBed($id: Long!, $bed: String, $roomId: Long) {
    assignBed(id: $id, bed: $bed, roomId: $roomId)
  }
`;

export const assignNurseInRoom = gql`
  mutation assignNurse($id: Long!, $staffId: Long, $roomId: Long) {
    assignNurse(id: $id, staffId: $staffId, roomId: $roomId)
  }
`;

export const editPreOpNote = gql`
  mutation editPreOpNote($id: Long!, $note: String) {
    editPreOpNote(id: $id, note: $note)
  }
`;

export const editPacuNote = gql`
  mutation editPacuNote($id: Long!, $note: String) {
    editPacuNote(id: $id, note: $note)
  }
`;

export const editCaretakerNote = gql`
  mutation editCaretakerNote($id: Long!, $note: String) {
    editCaretakerNote(id: $id, note: $note)
  }
`;

export const editBlockNerveFollow = gql`
  mutation editBlockNerveFollow($id: Long!, $blockFollow: Boolean) {
    editBlockNerveFollow(id: $id, blockFollow: $blockFollow)
  }
`;

export const assignPriority = gql`
  mutation assignPriority($id: Long!, $priority: String) {
    assignPriority(id: $id, priority: $priority)
  }
`;

export const setReady = gql`
  mutation setPatientReady($id: Long!) {
    setPatientReady(patientId: $id)
  }
`;

export const setFamilyReadyPACU = gql`
  mutation setFamilyReadyPACU($id: Long!) {
    setFamilyReadyPACU(patientId: $id)
  }
`;

export const setFamilyReadyPOSTOP = gql`
  mutation setFamilyReadyPOSTOP($id: Long!) {
    setFamilyReadyPOSTOP(patientId: $id)
  }
`;

export const setFamilyReadyForPickupPACU = gql`
  mutation setFamilyReadyForPickupPACU($id: Long!) {
    setFamilyReadyForPickupPACU(patientId: $id)
  }
`;

export const setFamilyReadyForPickupPOSTOP = gql`
  mutation setFamilyReadyForPickupPOSTOP($id: Long!) {
    setFamilyReadyForPickupPOSTOP(patientId: $id)
  }
`;

export const setFamilyReadyInPOSTOP = gql`
  mutation setFamilyReadyPOSTOP($id: Long!) {
    setFamilyReadyPOSTOP(patientId: $id)
  }
`;

export const setPatientReadyInPREP = gql`
  mutation setPatientReadyInPREP($id: Long!) {
    setPatientReadyInPREP(patientId: $id)
  }
`;

export const setPatientReadyInPACU = gql`
  mutation setPatientReadyInPACU($id: Long!) {
    setPatientReadyInPACU(patientId: $id)
  }
`;

export const setPatientReadyInPOSTOP = gql`
  mutation setPatientReadyInPOSTOP($id: Long!) {
    setPatientReadyInPOSTOP(patientId: $id)
  }
`;

export const setPatientNotReadyInPREP = gql`
  mutation setPatientNotReadyInPREP($id: Long!) {
    setPatientNotReadyInPREP(patientId: $id)
  }
`;

export const setPatientNotReadyInPACU = gql`
  mutation setPatientNotReadyInPACU($id: Long!) {
    setPatientNotReadyInPACU(patientId: $id)
  }
`;

export const setPatientNotReadyInPOSTOP = gql`
  mutation setPatientNotReadyInPOSTOP($id: Long!) {
    setPatientNotReadyInPOSTOP(patientId: $id)
  }
`;

export const setReadyForAnesthesia = gql`
  mutation setReadyForAnesthesia($id: Long!) {
    setReadyForAnesthesia(patientId: $id)
  }
`;

export const setNotReadyForAnesthesia = gql`
  mutation setNotReadyForAnesthesia($id: Long!) {
    setNotReadyForAnesthesia(patientId: $id)
  }
`;

export const setSpinalTimeout = gql`
  mutation setSpinalTimeout($id: Long!) {
    setSpinalTimeout(patientId: $id)
  }
`;

export const setUndoSpinalTimeout = gql`
  mutation setUndoSpinalTimeout($id: Long!) {
    setUndoSpinalTimeout(patientId: $id)
  }
`;

export const setPatientReadyForSurgeon = gql`
  mutation setPatientReadyForSurgeon($id: Long!) {
    setPatientReadyForSurgeon(patientId: $id)
  }
`;

export const setPatientNotReadyForSurgeon = gql`
  mutation setPatientNotReadyForSurgeon($id: Long!) {
    setPatientNotReadyForSurgeon(patientId: $id)
  }
`;

export const notifyReadyForSurgeon = gql`
  mutation notifyReadyForSurgeon($id: Long!) {
    notifyReadyForSurgeon(patientId: $id)
  }
`;

export const notifyReadyForAnesthesiologist = gql`
  mutation notifyReadyForAnesthesiologist($id: Long!) {
    notifyReadyForAnesthesiologist(patientId: $id)
  }
`;

export const setRoom = gql`
  mutation setRoomForPatient($id: Long!, $roomId: Long!) {
    setRoomForPatient(patientId: $id, roomId: $roomId)
  }
`;

export const resetProcedure = gql`
  mutation resetProcedure($id: Long!) {
    resetProcedure(id: $id)
  }
`;

export const startAdditionalTimeout = gql`
  mutation startAdditionalTimeout($id: Long!) {
    startAdditionalTimeout(id: $id)
  }
`;

export const exitProcedure = gql`
  mutation exitProcedure($id: Long!) {
    exitProcedure(id: $id)
  }
`;

export const preDischarge = gql`
  mutation preDischarge($id: Long!) {
    preDischarge(id: $id)
  }
`;

export const retainedBeaconDischargeEligibility = gql`
  query retainedBeaconDischargeEligibility($id: Long!) {
    retainedBeaconDischargeEligibility(id: $id) {
      eligible
      reason
      procedureId
      scheduledStart
      orId
      expiresAt
    }
  }
`;

export const recordRetainedBeaconEligibilityDegradation = gql`
  mutation recordRetainedBeaconEligibilityDegradation(
    $id: Long!
    $reason: String!
    $elapsedMs: Long
    $surface: String!
  ) {
    recordRetainedBeaconEligibilityDegradation(id: $id, reason: $reason, elapsedMs: $elapsedMs, surface: $surface)
  }
`;

export const discharge = gql`
  mutation discharge($id: Long!, $keepBeacon: Boolean = false) {
    discharge(id: $id, keepBeacon: $keepBeacon)
  }
`;

export const dischargePhysicianLastPatient = gql`
  mutation dischargePhysicianLastPatient($id: Long!, $keepBeacon: Boolean = false) {
    dischargePhysicianLastPatient(id: $id, keepBeacon: $keepBeacon)
  }
`;

export const dischargeAnesthesiologistLastPatient = gql`
  mutation dischargeAnesthesiologistLastPatient($id: Long!, $keepBeacon: Boolean = false) {
    dischargeAnesthesiologistLastPatient(id: $id, keepBeacon: $keepBeacon)
  }
`;

export const dischargeBothLastPatient = gql`
  mutation dischargeBothLastPatient($id: Long!, $keepBeacon: Boolean = false) {
    dischargeBothLastPatient(id: $id, keepBeacon: $keepBeacon)
  }
`;

export const transferToChair = gql`
  mutation transferToChair($id: Long!) {
    transferToChair(id: $id)
  }
`;

export const transferToPT = gql`
  mutation transferToPT($id: Long!) {
    transferToPT(id: $id)
  }
`;

export const ptComplete = gql`
  mutation ptComplete($id: Long!) {
    ptComplete(id: $id)
  }
`;

export const resendFamilyPageSms = gql`
  mutation resendFamilyPageSms($patientId: Long!) {
    resendFamilyPageSms(patientId: $patientId)
  }
`;

export const cancelVisit = gql`
  mutation cancelVisit($id: Long!, $reason: String) {
    cancelVisit(id: $id, reason: $reason)
  }
`;

export const setReadyToSeeFamilyPreOp = gql`
  mutation setReadyToSeeFamilyPreOp($id: Long!) {
    setReadyToSeeFamilyPreOp(patientId: $id)
  }
`;

export const setNotReadyToSeeFamilyPreOp = gql`
  mutation setNotReadyToSeeFamilyPreOp($id: Long!) {
    setNotReadyToSeeFamilyPreOp(patientId: $id)
  }
`;

export const setHoldProcedure = gql`
  mutation setHoldProcedure($id: Long!) {
    setHoldProcedure(patientId: $id)
  }
`;

export const setUndoHoldProcedure = gql`
  mutation setUndoHoldProcedure($id: Long!) {
    setUndoHoldProcedure(patientId: $id)
  }
`;

export const setBlockNerve = gql`
  mutation setBlockNerve($id: Long!) {
    setBlockNerve(patientId: $id)
  }
`;

export const setUndoBlockNerve = gql`
  mutation setUndoBlockNerve($id: Long!) {
    setUndoBlockNerve(patientId: $id)
  }
`;

export const setHelp = gql`
  mutation setHelp($id: Long!) {
    setHelp(patientId: $id)
  }
`;

export const setUndoHelp = gql`
  mutation setUndoHelp($id: Long!) {
    setUndoHelp(patientId: $id)
  }
`;

export const setFlipRoom = gql`
  mutation setFlipRoom($id: Long!) {
    setFlipRoom(patientId: $id)
  }
`;

export const setUndoFlipRoom = gql`
  mutation setUndoFlipRoom($id: Long!) {
    setUndoFlipRoom(patientId: $id)
  }
`;

export const setMedsDelivered = gql`
  mutation setMedsDelivered($id: Long!) {
    setMedsDelivered(patientId: $id)
  }
`;

export const setExternalPharmacy = gql`
  mutation setExternalPharmacy($id: Long!) {
    setExternalPharmacy(patientId: $id)
  }
`;

export const setUndoMedsDelivered = gql`
  mutation setUndoMedsDelivered($id: Long!) {
    setUndoMedsDelivered(patientId: $id)
  }
`;

export const editOrTimes = gql`
  mutation editOrTimes(
    $patientId: Long!
    $roomId: Long!
    $enteredAt: DateTime
    $timeoutStart: DateTime
    $procedureStart: DateTime
    $calledNextPatient: DateTime
    $closingStart: DateTime
    $surgeonLeft: DateTime
  ) {
    editOrTimes(
      patientId: $patientId
      roomId: $roomId
      enteredAt: $enteredAt
      timeoutStart: $timeoutStart
      procedureStart: $procedureStart
      calledNextPatient: $calledNextPatient
      closingStart: $closingStart
      surgeonLeft: $surgeonLeft
    )
  }
`;

export const editPatientOobWarningDelayReason = gql`
  mutation editPatientOobWarningDelayReason($id: Long!, $oobWarningDelayReason: String) {
    editPatientOobWarningDelayReason(id: $id, oobWarningDelayReason: $oobWarningDelayReason) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const editPatientPtWarningDelayReason = gql`
  mutation editPatientPtWarningDelayReason($id: Long!, $ptWarningDelayReason: String) {
    editPatientPtWarningDelayReason(id: $id, ptWarningDelayReason: $ptWarningDelayReason) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const editPatientPtCompleteWarningDelayReason = gql`
  mutation editPatientPtCompleteWarningDelayReason($id: Long!, $ptCompleteWarningDelayReason: String) {
    editPatientPtCompleteWarningDelayReason(id: $id, ptCompleteWarningDelayReason: $ptCompleteWarningDelayReason) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const editPatientPacuOutWarningDelayReason = gql`
  mutation editPatientPacuOutWarningDelayReason($id: Long!, $pacuOutWarningDelayReason: String) {
    editPatientPacuOutWarningDelayReason(id: $id, pacuOutWarningDelayReason: $pacuOutWarningDelayReason) {
      ...PatientData
    }
  }
  ${fragments.all}
`;

export const listSubscription = gql`
  subscription {
    waitingRoom {
      id
      name
      icon
      color
      changedRoomAt
      room {
        id
        name
        type
      }
      log {
        id
        entries {
          type
          room {
            id
            name
            type
          }
          manualEntryBy {
            id
            name
          }
          manualTimestamp
          status
        }
        timestamp
      }
    }
  }
`;

export default {
  list,
  listActiveAndPastPatients,
  item,
  setRoom,
  pastItem,
  create,
  update,
  remove,
  subscription,
  subscriptionWithPhysician,
};
